import styled from 'styled-components';
import React, { useContext, useMemo } from 'react';
import { useMatchRules } from '@/features/editor/widgets/price-test/use-match-rules';
import { QueryBuilder } from '@/components/query-builder';
import { QBItemSelection } from '@/components/query-builder/models';
import {
  getCurrencySymbol,
  PriceTestContext,
} from '@/features/editor/widgets/price-test/context';
import { QBCaption, QBTextInput } from '@/components/query-builder/shared';
import { SelectOptions, SmallSelect } from '@/components/small-select';

const OPTIONS: SelectOptions[] = [
  { key: `increasePercent`, value: `Increase Price by %` },
  { key: `decreasePercent`, value: `Decrease Price by %` },
  { key: `increaseAmount`, value: `Increase Price by $$` },
  { key: `decreaseAmount`, value: `Decrease Price by $$` },
];

export function PriceTestStepMatchRule() {
  const { props, setProps, currency } = useContext(PriceTestContext);

  const formattedOptions = useMemo(
    () =>
      OPTIONS.map((option) => ({
        ...option,
        value: option.value.replace(`$$`, getCurrencySymbol(currency)),
      })),
    [currency],
  );

  const defaultOffsetType = useMemo(() => {
    const offsetValue = props?.rules?.priceOffset;
    const offsetType = props?.rules?.offsetType;
    let prefix = ``;

    if (offsetValue >= 0) {
      prefix = `increase`;
    } else {
      prefix = `decrease`;
    }

    if (offsetType === `percent`) {
      return `${prefix}Percent`;
    }
    if (offsetType === `fixed`) {
      return `${prefix}Amount`;
    }

    return OPTIONS[0].key as string;
  }, [props?.rules]);

  const { condition } = useMatchRules();

  const onQueryBuilderChange = (value: QBItemSelection[]) => {
    setProps((draft) => {
      draft.rules.rules = value;
    });
  };

  const onOffsetTypeSelected = (selected: string) => {
    setProps((draft) => {
      if (selected.includes(`Percent`)) {
        draft.rules.offsetType = `percent`;
      }
      if (selected.includes(`Amount`)) {
        draft.rules.offsetType = `fixed`;
      }
      if (selected.includes(`increase`)) {
        draft.rules.priceOffset = Math.abs(draft.rules.priceOffset);
      } else {
        draft.rules.priceOffset = -1 * Math.abs(draft.rules.priceOffset);
      }
    });
  };

  const onOffsetChanged = (ev) => {
    const value = Math.abs(parseFloat(ev.target.value));
    setProps((draft) => {
      if (defaultOffsetType.includes(`increase`)) {
        draft.rules.priceOffset = value;
      } else {
        draft.rules.priceOffset = -1 * value;
      }
    });
  };

  return (
    <Wrapper>
      <QueryBuilder
        whereNotion="If"
        onChange={onQueryBuilderChange}
        initialProps={condition}
        initialState={props?.rules?.rules || []}
      />
      <div className="then-wrapper">
        <QBCaption>Then</QBCaption>
        <SmallSelect
          minWidth={200}
          maxWidth={200}
          options={formattedOptions}
          onSelection={onOffsetTypeSelected}
          defaultValue={defaultOffsetType}
        />
        <QBTextInput
          className="then-input"
          type="number"
          defaultValue={Math.abs(props?.rules?.priceOffset || 0)}
          onChange={onOffsetChanged}
        />
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .qb-row-wrapper {
    margin-top: 2rem;

    div[class*='vsly-small-select'] div[class*='control'] {
      min-width: 40px !important;
      max-width: 190px !important;
    }
  }

  div[class*='query-builder__Row'] {
    gap: 1rem !important;
  }

  .then-wrapper {
    margin-top: 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 2rem;
    padding-bottom: 2rem;
  }

  .then-input {
    min-width: 6rem;
    max-width: 6rem;
    text-align: center;
    padding-right: 0.2rem;
    padding-left: 0.2rem;
  }
`;
